import React, { useState, useEffect } from 'react';
import { getDashboardStats, API_BASE_URL, deleteTrainingRecord, deleteTrainingSession } from '../utils/api';
import axios from 'axios';
import './Dashboard.css';

// Helper function to format hours and minutes
const formatDuration = (hours) => {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);

  if (h === 0 && m === 0) return '0 hrs';
  if (h === 0) return `${m} mins`;
  if (m === 0) return `${h} hrs`;
  return `${h} hrs ${m} mins`;
};

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetails, setSessionDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);

  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await getDashboardStats();
      setStats(response.data);
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleEditRecord = (record, e) => {
    e.stopPropagation();
    setEditingRecord(record);
  };

  const closeEditModal = () => {
    setEditingRecord(null);
  };

  const saveEditRecord = async (formData) => {
    try {
      await axios.put(`${API_BASE_URL}/training/${editingRecord.id}`, formData);
      await loadStats();
      closeEditModal();
      alert('Training record updated successfully!');
    } catch (err) {
      console.error('Failed to update record:', err);
      alert('Failed to update training record');
    }
  };

  const handleDeleteRecord = async (recordId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this training record?')) {
      return;
    }

    setDeletingId(recordId);
    try {
      await axios.delete(`${API_BASE_URL}/training/${recordId}`);
      await loadStats();
      alert('Training record deleted successfully!');
    } catch (err) {
      console.error('Failed to delete record:', err);
      alert('Failed to delete training record');
    } finally {
      setDeletingId(null);
    }
  };

  const loadSessionDetails = async (sessionId) => {
    setLoadingDetails(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/training/session/${sessionId}`);
      setSessionDetails(response.data);
    } catch (err) {
      console.error('Failed to load session details:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleSessionClick = (session) => {
    setSelectedSession(session);
    loadSessionDetails(session.session_id);
  };

  const closeModal = () => {
    setSelectedSession(null);
    setSessionDetails(null);
  };

  // Filter and search logic
  const filterTraining = (records) => {
    if (!records) return [];

    return records.filter(record => {
      // Search query
      const matchesSearch = searchQuery === '' ||
        record.training_topic?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        record.employee_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        record.department?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        record.trainer_name?.toLowerCase().includes(searchQuery.toLowerCase());

      // Date range
      const recordDate = new Date(record.training_date);
      const matchesDateFrom = dateFrom === '' || recordDate >= new Date(dateFrom);
      const matchesDateTo = dateTo === '' || recordDate <= new Date(dateTo);

      // Department
      const matchesDepartment = departmentFilter === '' || record.department === departmentFilter;

      // Role
      const matchesRole = roleFilter === '' || record.role === roleFilter;

      return matchesSearch && matchesDateFrom && matchesDateTo && matchesDepartment && matchesRole;
    });
  };

  // Filter logic for Training Sessions (trainer-created sessions)
  const filterSessions = (sessions) => {
    if (!sessions) return [];

    return sessions.filter(session => {
      // Search query - match topic, trainer, or department
      const matchesSearch = searchQuery === '' ||
        session.training_topic?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        session.trainer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        session.trainer_department?.toLowerCase().includes(searchQuery.toLowerCase());

      // Date range
      const sessionDate = new Date(session.training_date);
      const matchesDateFrom = dateFrom === '' || sessionDate >= new Date(dateFrom);
      const matchesDateTo = dateTo === '' || sessionDate <= new Date(dateTo);

      // Department
      const matchesDepartment = departmentFilter === '' || session.trainer_department === departmentFilter;

      return matchesSearch && matchesDateFrom && matchesDateTo && matchesDepartment;
    });
  };

  const clearFilters = () => {
    setSearchQuery('');
    setDateFrom('');
    setDateTo('');
    setDepartmentFilter('');
    setRoleFilter('');
  };

  const handleDelete = async (recordId) => {
    if (!window.confirm('Are you sure you want to delete this training record?')) {
      return;
    }

    setDeletingId(recordId);
    try {
      await deleteTrainingRecord(recordId);
      // Reload session details
      if (selectedSession) {
        await loadSessionDetails(selectedSession.session_id);
      }
      // Reload stats
      await loadStats();
    } catch (err) {
      alert('Failed to delete training record');
      console.error('Delete error:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const handleDeleteSession = async (sessionId, e) => {
    e.stopPropagation(); // Prevent opening modal
    if (!window.confirm('Are you sure you want to delete this entire training session? All participants will be removed.')) {
      return;
    }

    setDeletingId(sessionId);
    try {
      await deleteTrainingSession(sessionId);
      // Close modal if open
      closeModal();
      // Reload stats
      await loadStats();
    } catch (err) {
      alert('Failed to delete training session');
      console.error('Delete session error:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const openPreview = (url) => {
    // Open Supabase file directly in new tab
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner"></div>
        <p>Loading data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <p>{error}</p>
        <button onClick={loadStats} className="retry-button">Try Again</button>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <span className="eyebrow-pill">Analytics</span>
        <h1 className="dashboard-title">
          Training <em>Overview</em>
        </h1>
      </div>

      {/* Filters Section */}
      <div className="filters-section">
        <div className="filters-row">
          <div className="filter-group">
            <label className="filter-label">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"></circle>
                <path d="m21 21-4.35-4.35"></path>
              </svg>
              Search
            </label>
            <input
              type="text"
              className="filter-input"
              placeholder="Search name, topic, department..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">From Date</label>
            <input
              type="date"
              className="filter-input"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">To Date</label>
            <input
              type="date"
              className="filter-input"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label className="filter-label">Department</label>
            <select
              className="filter-input"
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
            >
              <option value="">All Departments</option>
              {stats.byDepartment && stats.byDepartment.map((dept, idx) => (
                <option key={idx} value={dept.department}>{dept.department}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label className="filter-label">Role</label>
            <select
              className="filter-input"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="">All Roles</option>
              <option value="trainer">Trainer</option>
              <option value="trainee">Trainee</option>
            </select>
          </div>

          <button className="clear-filters-btn" onClick={clearFilters}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
            Clear
          </button>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#F2E3D6', color: '#C4612F' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalEmployees}</div>
            <div className="stat-label">Total Employees</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#d4edda', color: '#155724' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"></circle>
              <polyline points="12 6 12 12 16 14"></polyline>
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-value">{formatDuration(stats.totalHours)}</div>
            <div className="stat-label">Total Training Time</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#cce5ff', color: '#004085' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalRecords}</div>
            <div className="stat-label">Training Sessions</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ backgroundColor: '#fff3cd', color: '#856404' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="1" x2="12" y2="23"></line>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            </svg>
          </div>
          <div className="stat-content">
            <div className="stat-value">{formatDuration(stats.avgHoursPerEmployee)}</div>
            <div className="stat-label">Average per Employee</div>
          </div>
        </div>
      </div>

      <div className="dashboard-sections">
        <section className="dashboard-section">
          <h2 className="section-title">By Department</h2>
          <div className="department-list">
            {stats.byDepartment && stats.byDepartment.length > 0 ? (
              stats.byDepartment.map((dept, index) => (
                <div key={index} className="department-item">
                  <div className="department-info">
                    <div className="department-name">{dept.department}</div>
                    <div className="department-meta">
                      {dept.employee_count} {dept.employee_count === 1 ? 'employee' : 'employees'}
                    </div>
                  </div>
                  <div className="department-hours">
                    {formatDuration(dept.total_hours)}
                  </div>
                </div>
              ))
            ) : (
              <p className="empty-state">No data available</p>
            )}
          </div>
        </section>

        <section className="dashboard-section">
          <h2 className="section-title">Training Sessions</h2>
          <div className="sessions-grid">
            {filterSessions(stats.trainingSessions).length > 0 ? (
              filterSessions(stats.trainingSessions).map((session) => (
                <div
                  key={session.session_id}
                  className="session-card-dashboard"
                  onClick={() => handleSessionClick(session)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="session-header">
                    <div className="session-topic-main">{session.training_topic}</div>
                    <div className="session-header-right">
                      <div className="session-date">
                        {new Date(session.training_date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        })}
                      </div>
                      <button
                        className="session-delete-btn"
                        onClick={(e) => handleDeleteSession(session.session_id, e)}
                        disabled={deletingId === session.session_id}
                        title="Delete entire session"
                      >
                        {deletingId === session.session_id ? (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spinner-icon">
                            <circle cx="12" cy="12" r="10" opacity="0.25"></circle>
                            <path d="M4 12a8 8 0 018-8" opacity="0.75"></path>
                          </svg>
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="session-body">
                    <div className="session-trainer-info">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
                        <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
                      </svg>
                      <span>{session.trainer_name}</span>
                    </div>
                    <div className="session-stats">
                      <div className="stat-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                          <circle cx="9" cy="7" r="4"></circle>
                          <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"></path>
                        </svg>
                        <span>{session.trainee_count} {session.trainee_count === 1 ? 'trainee' : 'trainees'}</span>
                      </div>
                      <div className="stat-item">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                        <span>{formatDuration(session.training_hours)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="empty-state">No training sessions yet</p>
            )}
          </div>
        </section>

        <section className="dashboard-section">
          <h2 className="section-title">Recent Training</h2>
          <div className="training-list">
            {filterTraining(stats.recentTraining).length > 0 ? (
              filterTraining(stats.recentTraining).map((record) => (
                <div key={record.id} className="training-item">
                  <div className="training-main">
                    <div className="training-topic">{record.training_topic}</div>
                    <div className="training-meta">
                      {record.employee_name} · {record.department}
                      {record.role && (
                        <span className={`role-badge ${record.role}`}>
                          {record.role === 'trainer' ? '👨‍🏫 Trainer' : '👤 Trainee'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="training-details">
                    <div className="training-date">
                      {new Date(record.training_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </div>
                    <div className="training-hours-badge">
                      {formatDuration(record.training_hours)}
                    </div>
                    {record.attachment_url && (() => {
                      try {
                        const urls = JSON.parse(record.attachment_url);
                        if (Array.isArray(urls) && urls.length > 0) {
                          return (
                            <div className="attachments-group">
                              {urls.map((url, idx) => (
                                <button
                                  key={idx}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    openPreview(url);
                                  }}
                                  className="attachment-link"
                                  title={`View attachment ${idx + 1}`}
                                >
                                  📎{urls.length > 1 && <span className="attachment-count">{idx + 1}</span>}
                                </button>
                              ))}
                            </div>
                          );
                        }
                      } catch (e) {
                        // Fallback for old single URL format
                        return (
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              openPreview(record.attachment_url);
                            }}
                            className="attachment-link"
                            title="View attachment"
                          >
                            📎
                          </button>
                        );
                      }
                      return null;
                    })()}
                    <button
                      className="record-action-btn edit-btn"
                      onClick={(e) => handleEditRecord(record, e)}
                      title="Edit record"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                      </svg>
                    </button>
                    <button
                      className="record-action-btn delete-btn"
                      onClick={(e) => handleDeleteRecord(record.id, e)}
                      disabled={deletingId === record.id}
                      title="Delete record"
                    >
                      {deletingId === record.id ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spinner-icon">
                          <circle cx="12" cy="12" r="10" opacity="0.25"></circle>
                          <path d="M4 12a8 8 0 018-8" opacity="0.75"></path>
                        </svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6"></polyline>
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <p className="empty-state">No data available</p>
            )}
          </div>
        </section>
      </div>

      {/* Session Details Modal */}
      {selectedSession && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content session-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{selectedSession.training_topic}</h2>
              <button className="close-btn" onClick={closeModal}>×</button>
            </div>

            <div className="modal-body">
              <div className="session-info-row">
                <div className="info-item">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 10v6M2 10l10-5 10 5-10 5z"></path>
                    <path d="M6 12v5c3 3 9 3 12 0v-5"></path>
                  </svg>
                  <div>
                    <div className="info-label">Trainer</div>
                    <div className="info-value">{selectedSession.trainer_name}</div>
                  </div>
                </div>

                <div className="info-item">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                  </svg>
                  <div>
                    <div className="info-label">Date</div>
                    <div className="info-value">
                      {new Date(selectedSession.training_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </div>
                  </div>
                </div>

                <div className="info-item">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                  </svg>
                  <div>
                    <div className="info-label">Duration</div>
                    <div className="info-value">{formatDuration(selectedSession.training_hours)}</div>
                  </div>
                </div>
              </div>

              <div className="trainees-section">
                <h3 className="trainees-title">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                    <circle cx="9" cy="7" r="4"></circle>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"></path>
                  </svg>
                  Participants ({selectedSession.trainee_count})
                </h3>

                {loadingDetails ? (
                  <div className="loading-trainees">
                    <div className="spinner-small"></div>
                    <span>Loading participants...</span>
                  </div>
                ) : sessionDetails && sessionDetails.trainees.length > 0 ? (
                  <div className="trainees-list">
                    {sessionDetails.trainees.map((trainee, index) => (
                      <div key={trainee.id} className="trainee-item">
                        <div className="trainee-number">{index + 1}</div>
                        <div className="trainee-info">
                          <div className="trainee-name">{trainee.employee_name}</div>
                          <div className="trainee-meta">
                            {trainee.department} · ID: {trainee.employee_id || 'Guest'}
                          </div>
                        </div>
                        <div className="trainee-hours">
                          {formatDuration(trainee.training_hours)}
                        </div>
                        <button
                          className="delete-btn"
                          onClick={() => handleDelete(trainee.id)}
                          disabled={deletingId === trainee.id}
                          title="Delete this record"
                        >
                          {deletingId === trainee.id ? (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spinner-icon">
                              <circle cx="12" cy="12" r="10" opacity="0.25"></circle>
                              <path d="M4 12a8 8 0 018-8" opacity="0.75"></path>
                            </svg>
                          ) : (
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6"></polyline>
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                              <line x1="10" y1="11" x2="10" y2="17"></line>
                              <line x1="14" y1="11" x2="14" y2="17"></line>
                            </svg>
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-trainees">No participants yet</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Record Modal */}
      {editingRecord && (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="modal-content edit-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Training Record</h2>
              <button className="close-btn" onClick={closeEditModal}>×</button>
            </div>

            <div className="modal-body">
              <EditRecordForm
                record={editingRecord}
                onSave={saveEditRecord}
                onCancel={closeEditModal}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Edit Record Form Component
function EditRecordForm({ record, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    training_topic: record.training_topic || '',
    training_date: record.training_date || '',
    training_hours: record.training_hours || '',
    trainer_name: record.trainer_name || '',
    notes: record.notes || ''
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="edit-form">
      <div className="form-group">
        <label className="form-label">Training Topic</label>
        <input
          type="text"
          name="training_topic"
          value={formData.training_topic}
          onChange={handleChange}
          className="form-input"
          required
        />
      </div>

      <div className="form-group">
        <label className="form-label">Training Date</label>
        <input
          type="date"
          name="training_date"
          value={formData.training_date}
          onChange={handleChange}
          className="form-input"
          required
        />
      </div>

      <div className="form-group">
        <label className="form-label">Training Hours</label>
        <input
          type="number"
          name="training_hours"
          value={formData.training_hours}
          onChange={handleChange}
          className="form-input"
          step="0.5"
          min="0"
          required
        />
      </div>

      <div className="form-group">
        <label className="form-label">Trainer Name</label>
        <input
          type="text"
          name="trainer_name"
          value={formData.trainer_name}
          onChange={handleChange}
          className="form-input"
        />
      </div>

      <div className="form-group">
        <label className="form-label">Notes</label>
        <textarea
          name="notes"
          value={formData.notes}
          onChange={handleChange}
          className="form-input"
          rows="3"
        />
      </div>

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn-cancel">
          Cancel
        </button>
        <button type="submit" className="btn-save" disabled={saving}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </form>
  );
}

export default Dashboard;
